import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocSection,
  DocCard,
  DocAlert,
  DocCodeBlock,
  DocNavFooter,
} from "@/components/docs";
import { docsColors, docsComponents } from "@/lib/docs-theme";

export default function TcpMonitoringPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "Monitor Types", href: "/docs/monitors" },
    { label: "TCP Port Monitoring" },
  ];

  return (
    <DocLayout
      title="TCP Port Monitoring - Uptime Monitor Documentation"
      description="Learn how to monitor TCP ports for databases, mail servers, and custom services. Check port availability and connection status."
      canonical="https://uptime-monitor.io/docs/monitors/tcp"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="TCP Port Monitoring"
        description="Monitor the availability of TCP ports on your servers. Perfect for databases, mail servers, game servers, and any custom TCP-based services."
      />

      {/* Overview */}
      <DocSection>
        <DocAlert variant="info" className="p-6">
          <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
            What is TCP Monitoring?
          </h2>
          <p className={`${docsColors.text.body} mb-4`}>
            TCP monitoring checks if a specific port on your server is open and accepting
            connections. It attempts to establish a TCP connection to the specified host and port,
            verifying that your service is accessible.
          </p>
          <p className={docsColors.text.body}>
            Unlike HTTP monitoring, TCP monitoring works at a lower network level and can monitor
            any TCP-based service, not just web services.
          </p>
        </DocAlert>
      </DocSection>

      {/* Configuration */}
      <DocSection title="Configuration">
        <DocCard>
          <div className="space-y-6">
            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Host and Port</h3>
              <p className={`${docsColors.text.body} mb-3`}>
                Specify the hostname or IP address and the TCP port number to monitor.
              </p>
              <DocCodeBlock className="leading-relaxed">
                Format: tcp://hostname:port
                <br />
                <br />
                Examples:
                <br />
                • tcp://database.example.com:3306
                <br />
                • tcp://mail.example.com:25
                <br />
                • tcp://192.168.1.100:8080
                <br />• tcp://game-server.example.com:25565
              </DocCodeBlock>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                Connection Timeout
              </h3>
              <p className={docsColors.text.body}>
                Maximum time to wait for a successful TCP connection. Default is 10 seconds. Adjust
                based on network latency and service response times.
              </p>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Check Frequency</h3>
              <p className={docsColors.text.body}>
                How often to check the TCP port. Options range from 10 seconds to 60 minutes.
                Consider the criticality of the service when setting frequency.
              </p>
            </div>
          </div>
        </DocCard>
      </DocSection>

      {/* Common Ports */}
      <DocSection title="Common TCP Ports">
        <DocCard>
          <p className={`${docsColors.text.body} mb-4`}>Commonly monitored TCP ports:</p>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className={`font-medium ${docsColors.text.heading} mb-2 text-sm`}>Databases</h4>
              <ul className="space-y-1 text-sm">
                <li className={docsColors.text.body}>
                  <span className={`font-mono ${docsColors.text.muted}`}>3306</span> - MySQL
                </li>
                <li className={docsColors.text.body}>
                  <span className={`font-mono ${docsColors.text.muted}`}>5432</span> - PostgreSQL
                </li>
                <li className={docsColors.text.body}>
                  <span className={`font-mono ${docsColors.text.muted}`}>27017</span> - MongoDB
                </li>
                <li className={docsColors.text.body}>
                  <span className={`font-mono ${docsColors.text.muted}`}>6379</span> - Redis
                </li>
              </ul>
            </div>

            <div>
              <h4 className={`font-medium ${docsColors.text.heading} mb-2 text-sm`}>Mail</h4>
              <ul className="space-y-1 text-sm">
                <li className={docsColors.text.body}>
                  <span className={`font-mono ${docsColors.text.muted}`}>25</span> - SMTP
                </li>
                <li className={docsColors.text.body}>
                  <span className={`font-mono ${docsColors.text.muted}`}>587</span> - SMTP/TLS
                </li>
                <li className={docsColors.text.body}>
                  <span className={`font-mono ${docsColors.text.muted}`}>143</span> - IMAP
                </li>
                <li className={docsColors.text.body}>
                  <span className={`font-mono ${docsColors.text.muted}`}>993</span> - IMAPS
                </li>
              </ul>
            </div>

            <div>
              <h4 className={`font-medium ${docsColors.text.heading} mb-2 text-sm`}>Web & Apps</h4>
              <ul className="space-y-1 text-sm">
                <li className={docsColors.text.body}>
                  <span className={`font-mono ${docsColors.text.muted}`}>80</span> - HTTP
                </li>
                <li className={docsColors.text.body}>
                  <span className={`font-mono ${docsColors.text.muted}`}>443</span> - HTTPS
                </li>
                <li className={docsColors.text.body}>
                  <span className={`font-mono ${docsColors.text.muted}`}>8080</span> - Alt HTTP
                </li>
              </ul>
            </div>

            <div>
              <h4 className={`font-medium ${docsColors.text.heading} mb-2 text-sm`}>Other</h4>
              <ul className="space-y-1 text-sm">
                <li className={docsColors.text.body}>
                  <span className={`font-mono ${docsColors.text.muted}`}>22</span> - SSH
                </li>
                <li className={docsColors.text.body}>
                  <span className={`font-mono ${docsColors.text.muted}`}>3389</span> - RDP
                </li>
              </ul>
            </div>
          </div>
        </DocCard>
      </DocSection>

      {/* Use Cases */}
      <DocSection title="Use Cases">
        <div className="grid gap-4">
          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Database Monitoring</h3>
            <p className={`${docsColors.text.body} mb-3`}>
              Ensure your database servers are accepting connections:
            </p>
            <ul className={`space-y-1 text-sm ${docsColors.text.body} ml-6`}>
              <li>• Monitor primary and replica database servers</li>
              <li>• Check database cluster endpoints</li>
              <li>• Detect when database ports become unreachable</li>
              <li>• Get notified if firewall rules change unexpectedly</li>
            </ul>
          </DocCard>

          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
              Mail Server Monitoring
            </h3>
            <p className={`${docsColors.text.body} mb-3`}>Keep email services running smoothly:</p>
            <ul className={`space-y-1 text-sm ${docsColors.text.body} ml-6`}>
              <li>• Monitor SMTP port availability (25, 587, 465)</li>
              <li>• Check IMAP/POP3 port accessibility</li>
              <li>• Detect if mail server ports are blocked</li>
              <li>• Ensure secure ports remain open for clients</li>
            </ul>
          </DocCard>

          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
              Custom Application Monitoring
            </h3>
            <p className={`${docsColors.text.body} mb-3`}>Monitor any TCP-based service:</p>
            <ul className={`space-y-1 text-sm ${docsColors.text.body} ml-6`}>
              <li>• Game servers for player connectivity</li>
              <li>• Message queues and brokers</li>
              <li>• Custom protocols and services</li>
              <li>• Internal microservices</li>
            </ul>
          </DocCard>
        </div>
      </DocSection>

      {/* Best Practices */}
      <DocSection title="Best Practices">
        <DocCard>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Monitor from multiple regions</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Ensure global accessibility and detect regional network issues
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Set appropriate timeouts</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Consider network latency and normal connection times for your service
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Monitor all critical ports</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Don&apos;t just monitor HTTP - check databases, caches, and supporting services
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Use with HTTP monitoring</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Combine TCP and HTTP checks for complete application monitoring
                </p>
              </div>
            </li>
          </ul>
        </DocCard>
      </DocSection>

      {/* Limitations */}
      <DocSection title="Limitations">
        <DocAlert variant="warning" className="p-6">
          <h3 className="text-lg font-semibold mb-3">What TCP Monitoring Doesn&apos;t Check</h3>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <div>
                <strong>Application logic:</strong> Only verifies port is open, not that the
                application is functioning correctly
              </div>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <div>
                <strong>Authentication:</strong> Cannot verify login credentials or authentication
                mechanisms
              </div>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <div>
                <strong>Data integrity:</strong> Doesn&apos;t validate data or query responses
              </div>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <div>
                <strong>SSL/TLS:</strong> For encrypted connections, use HTTP/HTTPS monitoring
                instead
              </div>
            </li>
          </ul>
          <p className="text-sm mt-4">
            For application-level monitoring, consider using HTTP/HTTPS monitors with health check
            endpoints.
          </p>
        </DocAlert>
      </DocSection>

      <DocNavFooter
        prev={{ href: "/docs/monitors/http", text: "HTTP/HTTPS Monitoring" }}
        next={{ href: "/docs/monitors/ping", text: "Ping Monitoring" }}
      />
    </DocLayout>
  );
}
